'use client';

import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import { useLoyaltyApi } from '@packages/shared/src/hooks/useLoyaltyApi/useLoyaltyApi';
import type { ReactNode } from 'react';
import { useMemo, createContext, useState, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import useSWR from 'swr';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { DialogFooter } from '@packages/shared/src/ui/Dialog/DialogFooter/DialogFooter';
import { Button } from '@packages/shared/src/ui/Button/Button';
import type { DialogState } from './types';

const messages = defineMessages({
  PaybackHeadline: { defaultMessage: 'Mein PAYBACK', id: 'Payback.Headline' },
  DialogCancelButton: { defaultMessage: 'Abbrechen', id: 'Payback.Dialog.CancelButton' },
});

declare const PB: any;
type PaybackClient = {
  createModule: (
    options: {
      moduleType?: string;
      selector?: string;
      width?: number;
      height?: number;
      contextKey?: string;
    },
    overlayOptions?: { backdropDisabled?: boolean; height?: number; maximumWidth?: number },
  ) => void;

  createModuleInOverlay: (
    moduleOptions: {
      moduleType: 'entitlement';
      contextKey: 'overlayContext';
      options: { entitlementGroup: 'OIMIKI_MARKETING' };
      onSuccess: (client: unknown) => void;
    },
    overlayConfig: { backdropDisabled: boolean; height: number; maximumWidth: number },
  ) => void;
};
export interface PaybackClientProps {
  paybackClient?: PaybackClient;
  dialogState: DialogState;
  setDialogState: (dialogState: DialogState) => void;
  closeDialogHandler: () => void;
}

export const PaybackClientContext = createContext<PaybackClientProps>({
  paybackClient: undefined,
  dialogState: { dialogTitle: undefined, dialogContent: undefined, open: false },
  setDialogState: () => {},
  closeDialogHandler: () => {},
});

export interface PaybackClientProviderProps {
  children: ReactNode;
}
export const PaybackClientProvider = ({ children }: PaybackClientProviderProps) => {
  const [paybackClient, setPaybackClient] = useState<PaybackClient | undefined>();
  const [dialogState, setDialogState] = useState<DialogState>({
    dialogTitle: undefined,
    dialogContent: undefined,
    additionalDialogAction: undefined,
    open: false,
  });
  const loyaltyApi = useLoyaltyApi();
  const { loggedIn } = useLoginState();
  const { formatMessage, locale } = useIntl();

  useSWR(
    loyaltyApi && loggedIn ? 'loyaltySession' : null,
    () => loyaltyApi?.loyaltySession({ ecLocale: locale }),
    {
      revalidateOnFocus: false,
      onSuccess: async (data) => {
        if (data?.sessionToken) {
          // const apiKey = await getPaybackApiKey();

          const apiKey = (await loyaltyApi?.loyaltyIframe({ ecLocale: locale }))?.iframeKey;
          PB.mc.core.Bootstrap.createClient({
            apiKey,
            token: data.sessionToken,
            onTokenRequired(resolveCallback: (token?: string) => void) {
              resolveCallback(data.sessionToken);
            },
            onTokenTooWeak() {},
            onSuccess(client: PaybackClient) {
              setPaybackClient(client);
            },
          });
        }
      },
    },
  );
  const closeDialogHandler = useCallback(() => {
    setDialogState({
      dialogTitle: undefined,
      dialogContent: undefined,
      additionalDialogAction: undefined,
      open: false,
    });
  }, []);
  const value = useMemo(
    () => ({ paybackClient, dialogState, setDialogState, closeDialogHandler }),
    [paybackClient, dialogState, closeDialogHandler],
  );

  return (
    <PaybackClientContext.Provider value={value}>
      {children}
      {dialogState.open && (
        <Dialog onClose={closeDialogHandler} isOpen={dialogState.open}>
          <DialogHeader onClose={closeDialogHandler}>{dialogState.dialogTitle}</DialogHeader>
          <DialogContent className="p-[2px]">{dialogState.dialogContent}</DialogContent>
          <DialogFooter>
            <Button onClick={closeDialogHandler}>
              {formatMessage(messages.DialogCancelButton)}
            </Button>
            {dialogState.additionalDialogAction}
          </DialogFooter>
        </Dialog>
      )}
    </PaybackClientContext.Provider>
  );
};
